import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useTheme } from '@context/ThemeContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import InlineMapPicker from './InlineMapPicker';

function createMeetingPinEl() {
  const el = document.createElement('div');
  el.style.cssText =
    'width:28px;height:28px;border-radius:50%;' +
    'background:linear-gradient(135deg,#3b82f6,#7c3aed);' +
    'border:3px solid white;box-shadow:0 2px 10px rgba(59,130,246,0.5);' +
    'cursor:grab;touch-action:none;user-select:none;-webkit-user-select:none';
  return el;
}

const nearlySame = (a, b) => Math.abs(a - b) < 1e-7;

/**
 * Compact Mapbox map + draggable pin for forms (matches Map tab: Mapbox GL + same styles).
 * Falls back to Leaflet InlineMapPicker when MAPBOX_TOKEN is unset.
 */
const MapboxInlinePicker = ({ lat, lng, onCoordinatesChange }) => {
  const { mapboxToken } = usePublicConfig();
  const { dark } = useTheme();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const onChangeRef = useRef(onCoordinatesChange);
  const darkRef = useRef(dark);

  onChangeRef.current = onCoordinatesChange;
  darkRef.current = dark;

  useEffect(() => {
    if (!mapboxToken || !containerRef.current) return;

    mapboxgl.accessToken = mapboxToken;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: darkRef.current
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: 14,
      attributionControl: false,
      maxZoom: 18,
      minZoom: 2,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    const pin = createMeetingPinEl();
    const marker = new mapboxgl.Marker({ element: pin, draggable: true, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(map);

    marker.on('dragstart', () => {
      pin.style.cursor = 'grabbing';
    });
    marker.on('dragend', () => {
      pin.style.cursor = 'grab';
      const ll = marker.getLngLat();
      onChangeRef.current([ll.lng, ll.lat]);
    });

    map.on('click', (e) => {
      const { lng: ln, lat: lt } = e.lngLat;
      marker.setLngLat([ln, lt]);
      onChangeRef.current([ln, lt]);
    });

    map.on('load', () => setMapReady(true));

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      setMapReady(false);
      marker.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map mounts once per token; center sync handled below
  }, [mapboxToken]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    const next = dark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12';
    mapRef.current.setStyle(next);
  }, [dark, mapReady]);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !mapReady) return;
    const cur = markerRef.current.getLngLat();
    if (nearlySame(cur.lat, lat) && nearlySame(cur.lng, lng)) return;
    markerRef.current.setLngLat([lng, lat]);
    mapRef.current.easeTo({ center: [lng, lat], duration: 450 });
  }, [lat, lng, mapReady]);

  if (!mapboxToken) {
    return (
      <InlineMapPicker lat={lat} lng={lng} onCoordinatesChange={onCoordinatesChange} />
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-surface-200 dark:border-surface-700">
      <div ref={containerRef} className="h-52 w-full" />
      <p className="bg-surface-50 px-3 py-2 text-[11px] text-surface-500 dark:bg-surface-900/80 dark:text-surface-400">
        Tap the map or drag the pin to set the meeting point.
      </p>
    </div>
  );
};

export default MapboxInlinePicker;
